import type { RecordStocktakeDto } from '@repo/api-client';
import { z } from 'zod';

import { INSTANT_MALFORMED, isoInstantFrom } from '@/lib/local-instant';

/**
 * What the stocktake form accepts, and what it sends.
 *
 * **The whole of this file is one rule stated in code: a person enters what they
 * counted, and nothing else.** There is no field for a difference, no `+/-`, and
 * no place for the figure the system believes — that last one is not merely
 * absent from the schema, it never reaches the screen at all (`RecordStocktake`
 * says why where it strips it). The route exists because a person asked for a
 * difference mistypes a sign and silently corrupts a level; the arithmetic is the
 * server's (CL-503, FR-539b), and a form that offered the shape would make the
 * mistake reachable however careful the copy around it was.
 *
 * ## Empty and zero are different answers
 *
 * A count box left empty means **nobody counted that item** — it is left out of
 * the submission entirely. `0` means **the shelf was found empty**, which is a
 * real count, is accepted by the route (`MIN_COUNTED_QUANTITY` is `0` there,
 * against the delivery and correction floor of `1`), and is precisely the count
 * that finds a whole shelf missing. Collapsing the two would either submit counts
 * nobody took or refuse the most useful count on the form.
 *
 * ## What is deliberately not here
 *
 * The idempotency attempt — the key, the body's `id`, and the rule that holds one
 * while the payload stands and mints a fresh one when it moves — is
 * `WriteAttempt` and `attemptFor` in `lib/write-attempt.ts`, which FE037
 * consolidated out of this epic's first two forms and which states the argument
 * at length. This form is the third consumer rather than the third copy; only
 * {@link fingerprintOf} is local, because it is the one part typed to this
 * route's own body. The moment field's prefill is `localInstantOf` in
 * `lib/local-instant.ts`, beside the parser that reads the same shape back.
 */

/**
 * The ceiling the route states, restated as the form's own (R-509, FR-533).
 *
 * There is no floor constant beside it, and its absence is the rule: a count is
 * matched against digits, so `0` is accepted and `-1` never parses. The API's own
 * floor is `0` for exactly the reason above, which is where this differs from
 * `correction-form.ts` — that form's floor is `1`, because a correction of
 * nothing is not a correction.
 */
const MAX_COUNTED_QUANTITY = 1_000_000;

/**
 * How many distinct items one stocktake may name.
 *
 * The route's own bound (`MAX_STOCKTAKE_COUNTS`), restated so that a shelf list
 * longer than it says so where the counts are rather than earning a `400
 * VALIDATION_FAILED` naming an array property. It bites on the *entered* counts
 * and not on the rows on screen: an outlet may stock more items than one
 * stocktake may name, and the boxes left empty cost nothing.
 */
const MAX_COUNTS = 500;

export const OUTLET_REQUIRED = 'Choose the outlet whose shelf was counted.';

export const WHEN_REQUIRED = 'Say when the shelf was counted.';

/**
 * **The message a minus sign earns.**
 *
 * A negative count is refused `422 STOCK_QUANTITY_INVALID`, which is final and
 * correct, but it arrives after the whole form has been sent and the thing it has
 * to teach is not "that number is out of range". It is that this field is not a
 * difference: you cannot count minus three dolls, however far below zero the
 * records may have gone. The copy therefore spends its second half on the
 * distinction the field actually turns on — an empty box against a `0`.
 */
export const COUNT_WHOLE =
  'A count is a whole number of items, digits only — no minus sign and no decimal point. This is what you counted on the shelf, never a difference from what the system believed. Leave the box empty for an item nobody counted; enter 0 for a shelf you found empty.';

export const COUNT_TOO_LARGE = `A count is at most ${new Intl.NumberFormat('en-IN').format(MAX_COUNTED_QUANTITY)} items.`;

export const NOTHING_COUNTED =
  'Enter what you counted for at least one item. An empty box is an item nobody counted, which is not the same as a shelf found empty — that is a 0.';

export const TOO_MANY_COUNTS = `Record stock takes at most ${String(MAX_COUNTS)} items at a time. Record the rest as a second count.`;

/**
 * An impossible state, worded for the person it would reach anyway.
 *
 * Nobody types a `catalogueItemId`: the rows are built from what the outlet
 * stocks. A blank one therefore means the rows and the item list have parted
 * company, which is a bug rather than a mistake — but a body carrying it earns a
 * `400` about an array property, so it is caught here and said in words.
 */
const ROW_LOST_ITS_ITEM =
  'This row has lost the item it was for. Reload the screen and count again.';

/** Digits and nothing else: no sign, no point, no exponent, no spaces. */
const WHOLE_NUMBER = /^\d+$/;

/** One row of the form: an item, and the box holding what was counted. */
export interface StocktakeCountValue {
  readonly catalogueItemId: string;
  readonly countedQuantity: string;
}

/** Whether somebody has actually answered for this item. */
export const isCounted = (row: StocktakeCountValue): boolean => row.countedQuantity.trim() !== '';

/**
 * The rows that carry a count, in the order they are on screen.
 *
 * The single place "an empty box is not a zero" is decided. Both the submission
 * and the running total in the rail read it, so the number somebody watches climb
 * is the number that will be sent.
 */
export const countsEntered = <Row extends StocktakeCountValue>(rows: readonly Row[]): Row[] =>
  rows.filter(isCounted);

/**
 * One row: which item, and what was counted on its shelf.
 *
 * `countedQuantity` is a string because that is what an `<Input>` holds, and
 * deliberately **not** `z.coerce.number()` — coercion reads `''` as `0`, which on
 * this form is the one mistake that cannot be allowed to happen silently: every
 * item nobody counted would be submitted as an empty shelf, and every one of them
 * would write a movement taking the outlet's whole holding away.
 *
 * `''` is therefore a valid value here and means "not counted". It is the
 * submission that leaves those rows out ({@link countsEntered}), and the form as
 * a whole that insists on at least one.
 *
 * `catalogueItemId` is never typed by anybody: the rows are built from what the
 * outlet stocks, so this cannot be blank unless that construction breaks. It is
 * checked anyway, because a blank id reaching the wire is a `400` about a field
 * nobody can see.
 */
const countSchema = z.object({
  catalogueItemId: z.string().min(1, ROW_LOST_ITS_ITEM),
  countedQuantity: z
    .string()
    .trim()
    .refine((digits) => digits === '' || WHOLE_NUMBER.test(digits), COUNT_WHOLE)
    .refine((digits) => digits === '' || Number(digits) <= MAX_COUNTED_QUANTITY, COUNT_TOO_LARGE),
});

/**
 * When the shelf was counted.
 *
 * Built from `lib/local-instant.ts`'s parser rather than from its
 * `localInstantSchema`, and only the *malformed* message is borrowed: that one is
 * about calendars and is true wherever a person types a date, while its required
 * message reads "choose the date and time to look up", which belongs to the two
 * screens that look something up. This field is a fact being recorded.
 */
const whenSchema = z
  .string()
  .trim()
  .min(1, WHEN_REQUIRED)
  .refine((local) => isoInstantFrom(local) !== null, INSTANT_MALFORMED);

/**
 * The whole form.
 *
 * **No upper bound on `occurredAt`, deliberately**, for the reason
 * `correction-form.ts` gives at length: the device's clock is not the server's,
 * and a form stricter than the route it posts to would refuse a true answer from
 * a terminal running a few minutes fast. What orders anything is the server's
 * `recordedAt` (FR-009, R-508); this field is the human fact beside it, and a
 * shelf walked on Tuesday morning and entered on Tuesday evening has to be able
 * to say so.
 *
 * **No duplicate check, unlike the correction form**, and the absence is
 * structural rather than an omission: there is exactly one row per item the
 * outlet stocks and nobody can add another, so the same item cannot appear twice.
 * `STOCK_DUPLICATE_ITEM_IN_REQUEST` stays mapped for the wire regardless — the
 * screen is not the only thing that can construct a body.
 */
export const stocktakeSchema = z
  .object({
    outletId: z.string().min(1, OUTLET_REQUIRED),
    at: whenSchema,
    counts: z.array(countSchema),
  })
  .superRefine((values, ctx) => {
    const entered = countsEntered(values.counts);

    // On the array rather than on a row: "you have not counted anything" is not
    // a statement about any one item, and attaching it to the first row would
    // put a red box under an item the person may never have intended to count.
    if (entered.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['counts'], message: NOTHING_COUNTED });
    }

    if (entered.length > MAX_COUNTS) {
      ctx.addIssue({ code: 'custom', path: ['counts'], message: TOO_MANY_COUNTS });
    }
  });

export type StocktakeValues = z.infer<typeof stocktakeSchema>;

/**
 * A row for an item, counted by nobody yet.
 *
 * **Pre-fills nothing**, which is the ticket's own instruction and the reason the
 * screen reads the outlet's items at all: the list is what tells somebody which
 * shelves to walk, and a box arriving with a number in it is a box somebody
 * confirms rather than a shelf somebody counts.
 */
export const blankCount = (catalogueItemId: string): StocktakeValues['counts'][number] => ({
  catalogueItemId,
  countedQuantity: '',
});

/**
 * What this form is about to send, as one string.
 *
 * It covers everything the API fingerprints — `{params, body}` — with the single
 * exception of the body's own `id`, and that exception is not a gap: the id is
 * minted *with* the key by `attemptFor` and is therefore a function of this
 * value. Two attempts with equal fingerprints carry the same id as well as the
 * same key, so the raw bodies are identical and the retry replays.
 *
 * `JSON.stringify` over an array rather than over the object, so the comparison
 * cannot turn on key order.
 */
export function fingerprintOf(outletId: string, body: Omit<RecordStocktakeDto, 'id'>): string {
  return JSON.stringify([outletId, body.occurredAt, body.counts]);
}
